import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { WorkoutPlan, PlanExercise } from '@/lib/types';

interface Options {
  studentId?: string;
  planId?: string;
  personalId?: string;
}

export function useWorkoutPlans(options: Options = {}) {
  const queryClient = useQueryClient();
  const { studentId, planId, personalId } = options;

  const plansQuery = useQuery({
    queryKey: ['workout-plans', { studentId, personalId }],
    queryFn: async () => {
      let query = supabase.from('workout_plans').select('*');
      if (studentId) query = query.eq('student_id', studentId) as any;
      if (personalId) query = query.eq('personal_id', personalId) as any;
      const { data, error } = await (query as any).order('created_at', { ascending: false });
      if (error) throw error;
      return data as WorkoutPlan[];
    },
    enabled: !!(studentId || personalId),
  });

  const exercisesQuery = useQuery({
    queryKey: ['plan-exercises', planId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('plan_exercises')
        .select('*, exercise:exercises(*)')
        .eq('plan_id', planId!)
        .order('order');
      if (error) throw error;
      return data as PlanExercise[];
    },
    enabled: !!planId,
  });

  const { mutateAsync: createPlan } = useMutation({
    mutationFn: async (input: { name: string; student_id: string; personal_id: string; routine_id?: string }) => {
      const { data, error } = await supabase.from('workout_plans').insert(input).select().single();
      if (error) throw error;
      return data as WorkoutPlan;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['workout-plans'] }),
  });

  const { mutateAsync: addExerciseToPlan } = useMutation({
    mutationFn: async ({ planId: pid, exerciseId, sets, reps, suggestedLoad, order }: {
      planId: string; exerciseId: string; sets: number; reps: number; suggestedLoad: number | null; order: number;
    }) => {
      const { data, error } = await supabase.from('plan_exercises').insert({
        plan_id: pid, exercise_id: exerciseId, sets, reps, suggested_load: suggestedLoad, order,
      }).select('*, exercise:exercises(*)').single();
      if (error) throw error;
      return data as PlanExercise;
    },
    onSuccess: (_, vars) => queryClient.invalidateQueries({ queryKey: ['plan-exercises', vars.planId] }),
  });

  const { mutateAsync: removeExerciseFromPlan } = useMutation({
    mutationFn: async (planExerciseId: string) => {
      const { error } = await supabase.from('plan_exercises').delete().eq('id', planExerciseId);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['plan-exercises', planId] }),
  });

  const loading = plansQuery.isLoading || exercisesQuery.isLoading;

  return {
    plans: plansQuery.data ?? [],
    planExercises: exercisesQuery.data ?? [],
    loading,
    createPlan,
    addExerciseToPlan: (pid: string, exerciseId: string, sets: number, reps: number, suggestedLoad: number | null, order: number) =>
      addExerciseToPlan({ planId: pid, exerciseId, sets, reps, suggestedLoad, order }),
    removeExerciseFromPlan,
    refetch: plansQuery.refetch,
  };
}
